import { useState } from 'react'
import { message, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import roleService, { type CreateRoleData, type UpdateRoleData } from '../services/role.service'
import type { Role } from '../types/role.types'

export const useRoleActions = () => {
    const [isFormModalOpen, setIsFormModalOpen] = useState(false)
    const [editingRole, setEditingRole] = useState<Role | null>(null)
    const queryClient = useQueryClient()

    // Create role mutation
    const createMutation = useMutation({
        mutationFn: (data: CreateRoleData) => roleService.createRole(data),
    })

    // Update role mutation
    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateRoleData }) => 
            roleService.updateRole(id, data),
    })

    // Delete role mutation
    const deleteMutation = useMutation({
        mutationFn: (id: number) => roleService.deleteRole(id),
        onSuccess: async () => {
            await queryClient.refetchQueries({ queryKey: ['roles'] })
            message.success('Role deleted successfully!')
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to delete role')
        },
    })

    const handleCreateRole = () => {
        setEditingRole(null)
        setIsFormModalOpen(true)
    }

    const handleEditRole = async (role: Role) => {
        try {
            // Fetch full role details with permissions
            const roleDetails = await roleService.getRole(role.id)
            
            // Transform permissions array to array of permission IDs
            const permissionIds = roleDetails.permissions?.map(p => p.id) || []
            
            setEditingRole({
                ...roleDetails,
                permissions: permissionIds
            })
            setIsFormModalOpen(true)
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Failed to load role details')
        }
    }

    const handleDeleteRole = (role: Role) => {
        if (role.is_system) {
            message.warning('System roles cannot be deleted')
            return
        }

        Modal.confirm({
            title: 'Delete Role',
            content: `Are you sure you want to delete "${role.display_name}"? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            onOk: () => {
                deleteMutation.mutate(role.id)
            },
        })
    }

    const handleFormSubmit = async (values: any) => {
        const permissionIds: number[] = values.permissions || []

        try {
            let roleId: number

            if (editingRole) {
                // Update existing role
                await updateMutation.mutateAsync({
                    id: editingRole.id,
                    data: {
                        name: values.name,
                        display_name: values.display_name,
                        description: values.description,
                    },
                })
                roleId = editingRole.id
            } else {
                // Create new role
                const created = await createMutation.mutateAsync({
                    name: values.name,
                    display_name: values.display_name,
                    description: values.description,
                })
                roleId = created.id
            }

            // Persist the selected permissions to the role
            if (permissionIds.length > 0) {
                await roleService.assignPermissions(roleId, { permission_ids: permissionIds })
            }

            await queryClient.refetchQueries({ queryKey: ['roles'] })
            message.success(editingRole ? 'Role updated successfully!' : 'Role created successfully!')
            setIsFormModalOpen(false)
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Failed to save role')
        }
    }

    const closeFormModal = () => setIsFormModalOpen(false)

    return {
        isFormModalOpen,
        editingRole,
        handleCreateRole,
        handleEditRole,
        handleDeleteRole,
        handleFormSubmit,
        closeFormModal,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
    }
}
